import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { startLogin, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Leaf, LogOut, Luggage, Home, User, Shield } from "lucide-react";

export const Logo = ({ testid = "nav-logo" }) => (
  <Link to="/" data-testid={testid} className="flex items-center gap-2 group">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white transition-transform duration-300 group-hover:-rotate-6">
      <Leaf className="h-5 w-5" />
    </span>
    <span className="font-display text-xl font-bold tracking-tight">FairStay</span>
  </Link>
);

const navCls = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-100"}`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 border-b bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/search" className={navCls} data-testid="nav-explore-link">Explore</NavLink>
          {user && <NavLink to="/trips" className={navCls} data-testid="nav-trips-link">Trips</NavLink>}
          {user && <NavLink to="/host" className={navCls} data-testid="nav-host-link">Hosting</NavLink>}
          {user?.is_admin && <NavLink to="/admin" className={navCls} data-testid="nav-admin-link">Admin</NavLink>}
        </nav>
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border bg-white p-1 pr-3 transition-shadow hover:shadow-md">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={imgUrl(user.picture)} />
                  <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:block">{user.name?.split(" ")[0]}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/users/${user.user_id}`)}><User className="mr-2 h-4 w-4" />Profile</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-trips" onClick={() => navigate("/trips")}><Luggage className="mr-2 h-4 w-4" />Trips</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-host" onClick={() => navigate("/host")}><Home className="mr-2 h-4 w-4" />Hosting</DropdownMenuItem>
              {user.is_admin && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><Shield className="mr-2 h-4 w-4" />Admin</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button data-testid="nav-signin-button" onClick={startLogin} className="rounded-full px-5">Sign in with Google</Button>
        )}
      </div>
    </header>
  );
}
